import React, { useMemo, useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StoreService from '../../services/storeService';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ImagePickerField from '../common/ImagePickerField';
import { useToast } from '../../context/ToastContext';
import {
  STORE_IMAGE_CATEGORY_CUSTOMER_LOGO,
  STORE_IMAGE_CATEGORY_LOGIN_BACKGROUND,
  STORE_IMAGE_CATEGORY_LOGIN_LOGO,
  STORE_IMAGE_CATEGORY_MANAGEMENT_LOGO,
  STORE_IMAGE_CATEGORY_FAVICON,
} from '../../constants/storeImageCategories';

const BRANDING_IMAGE_FIELDS = [
  {
    key: 'managementLogo',
    category: STORE_IMAGE_CATEGORY_MANAGEMENT_LOGO,
    cropAspect: 3,
  },
  {
    key: 'customerLogo',
    category: STORE_IMAGE_CATEGORY_CUSTOMER_LOGO,
    cropAspect: 3,
  },
  {
    key: 'loginLogo',
    category: STORE_IMAGE_CATEGORY_LOGIN_LOGO,
    cropAspect: 3,
  },
  {
    key: 'loginBackground',
    category: STORE_IMAGE_CATEGORY_LOGIN_BACKGROUND,
  },
  {
    key: 'favicon',
    category: STORE_IMAGE_CATEGORY_FAVICON,
    cropAspect: 1,
  },
];

const getImageCategory = (image) => image?.category;

const findImage = (images, category) => (images || []).find((image) => {
  const imageCategory = getImageCategory(image);
  const field = BRANDING_IMAGE_FIELDS.find((item) => item.category === category);
  return imageCategory === category
    || String(imageCategory).toLowerCase() === field?.key.toLowerCase();
});

const StoreBrandingImagesPanel = ({
  storeId,
  store,
  allowsCustomBranding,
  onStoreChanged,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const [processingKey, setProcessingKey] = useState(null);

  const images = useMemo(() => store?.images || [], [store?.images]);
  const canAdd = allowsCustomBranding && hasPermission(Permissions.StoresImagesAdd);
  const canDelete = allowsCustomBranding && hasPermission(Permissions.StoresImagesDelete);
  const canEdit = canAdd || canDelete;

  const refresh = async () => {
    if (onStoreChanged) {
      await onStoreChanged();
    }
  };

  const handleSelect = (field) => async (file) => {
    if (!canAdd || !file) return;

    try {
      setProcessingKey(field.key);
      await StoreService.addImages(storeId, [{ file }], field.category);
      await refresh();
      showSuccess(t('stores.branding.images.saved'));
    } catch (error) {
      console.error('Error uploading branding image:', error);
      showError(error.response?.data?.message || t('stores.branding.images.error.uploading'));
    } finally {
      setProcessingKey(null);
    }
  };

  const handleRemove = (field, image) => async () => {
    if (!canDelete || !image?.id) return;

    try {
      setProcessingKey(field.key);
      await StoreService.deleteImage(storeId, image.id);
      await refresh();
      showSuccess(t('stores.branding.images.deleted'));
    } catch (error) {
      console.error('Error deleting branding image:', error);
      showError(error.response?.data?.message || t('stores.branding.images.error.deleting'));
    } finally {
      setProcessingKey(null);
    }
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography component="h3" variant="h6" sx={{ fontWeight: 700 }}>
        {t('stores.branding.images.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3, maxWidth: 720 }}>
        {t('stores.branding.images.description')}
      </Typography>

      {allowsCustomBranding && !canEdit ? (
        <Alert severity="info" sx={{ mb: 3 }}>{t('stores.branding.readOnly')}</Alert>
      ) : null}

      <Grid container spacing={{ xs: 3, md: 4 }}>
        {BRANDING_IMAGE_FIELDS.map((field) => {
          const image = findImage(images, field.category);
          const busy = processingKey === field.key;
          const label = t(`stores.branding.images.${field.key}.label`);

          return (
            <Grid key={field.key} size={{ xs: 12, sm: 6 }}>
              <ImagePickerField
                label={label}
                helpText={t(`stores.branding.images.${field.key}.help`)}
                imageUrl={image?.url}
                onSelect={handleSelect(field)}
                onRemove={handleRemove(field, image)}
                disabled={!canEdit}
                processing={busy}
                cropAspect={field.cropAspect}
                removeConfirmMessage={t('stores.branding.images.confirmDelete')}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default StoreBrandingImagesPanel;
